// SB6-ANA-FILT-05 — Global Data Point Filter state.
//
// Holds the 7-axis selection that every Analysis widget reduces to
// (L1 Status → L7 Severity). This replaces the legacy 4-axis
// `analysisFilterStore` cascade for the data-point-keyed filter; the
// legacy store stays for any defect-projection consumers that haven't
// migrated.
//
// Each axis is a list of stable option keys. An empty list means
// "bypass" (no narrowing on that level) — EXCEPT the absence of a
// `status` value, which the BE interprets as ACTIVE-only. The FE always
// carries an explicit status (the default selection is `["ACTIVE"]`).
//
// Persistence (D6): the selection is stored per-user in
// `LysePreferences.globalDataPointFilter` (loaded on entry, never
// auto-reset). URL search params take precedence when present, then
// persist. The store is the single in-memory source of truth; the
// `useDataPointFilter` hook owns the URL ↔ store ↔ prefs bridge.
import { create } from "zustand";
import type { DataPointFilterSelection } from "@aeriosiq/types";

export const DEFAULT_SELECTION: DataPointFilterSelection = {
  status: ["ACTIVE"],
  reviewState: [],
  kind: [],
  defectType: [],
  component: [],
  location: [],
  severity: [],
};

/** Returns a fresh copy of the ACTIVE-only default selection. */
export function defaultSelection(): DataPointFilterSelection {
  return {
    status: ["ACTIVE"],
    reviewState: [],
    kind: [],
    defectType: [],
    component: [],
    location: [],
    severity: [],
  };
}

export function selectionsEqual(
  a: DataPointFilterSelection,
  b: DataPointFilterSelection,
): boolean {
  const axes: (keyof DataPointFilterSelection)[] = [
    "status",
    "reviewState",
    "kind",
    "defectType",
    "component",
    "location",
    "severity",
  ];
  for (const axis of axes) {
    const av = [...a[axis]].sort();
    const bv = [...b[axis]].sort();
    if (av.length !== bv.length) return false;
    for (let i = 0; i < av.length; i += 1) {
      if (av[i] !== bv[i]) return false;
    }
  }
  return true;
}

interface DataPointFilterState {
  selection: DataPointFilterSelection;
  /** Whether the in-memory selection has been hydrated from URL/prefs. */
  hydrated: boolean;
  /**
   * SB6-ANA-PROG-02 — monotonic filter-revision counter. Bumped on every
   * selection change AND on every committed AI-config change
   * (`aiFilterStore.setCommittedFilter`, which calls `bumpRevision`). Each
   * dependent query result is tagged with the revision it answered; a
   * widget is "in sync" iff its displayed data's revision === this latest
   * value. `isFetching` alone is insufficient — a queued-but-not-yet-fired
   * refetch must also count as out-of-sync, and a revision mismatch covers
   * that gap (the store value moved ahead of the displayed payload before
   * the new request even starts).
   */
  revision: number;
  setAxis: (
    axis: keyof DataPointFilterSelection,
    next: readonly string[],
  ) => void;
  setSelection: (next: DataPointFilterSelection) => void;
  resetToDefault: () => void;
  markHydrated: () => void;
  /**
   * Increment the revision without changing the selection. Used by the AI
   * filter store on a committed `aiClassFilter` change — that change refires
   * the data-point + facet queries (the AI filter is folded into their
   * params) but does not touch the 7-axis selection itself.
   */
  bumpRevision: () => void;
}

export const useDataPointFilterStore = create<DataPointFilterState>((set) => ({
  selection: defaultSelection(),
  hydrated: false,
  revision: 0,
  setAxis: (axis, next) => {
    set((s) => ({
      selection: { ...s.selection, [axis]: [...next] },
      revision: s.revision + 1,
    }));
  },
  setSelection: (next) => {
    set((s) => ({ selection: next, revision: s.revision + 1 }));
  },
  resetToDefault: () => {
    set((s) => ({ selection: defaultSelection(), revision: s.revision + 1 }));
  },
  markHydrated: () => {
    set({ hydrated: true });
  },
  bumpRevision: () => {
    set((s) => ({ revision: s.revision + 1 }));
  },
}));
